import React, { ChangeEvent, useState } from "react";
import { Button } from "@/components/Button";
import { DatePicker } from "@/components/DatePicker";
import { addMonthsToDate, getIsoDateFormat } from "@/helpers/utils";
import { IntervalSelector } from "../../IntervalSelector";
import {
  useChargeDate,
  useSubscriptionInterval,
  useSelectedItemContext,
} from "../../SelectedItemContext";
import { DrawerPageProps } from "../types";

export type UpdateIntervalPageProps = DrawerPageProps;

const ShipmentFrequency = ({
  notify,
  frequency,
}: Pick<UpdateIntervalPageProps, "notify"> & { frequency: number }) => {
  const order = useSelectedItemContext().boxItem;
  const intervalUpdator = useSubscriptionInterval();
  const [newFrequency, setNewFrequency] = useState(frequency);

  const upcomingDateStrings = new Array(3).fill(0).map((x, i) => {
    return addMonthsToDate(
      order.rechargeItem.nextChargeDate,
      newFrequency * (i + 1)
    ).toLocaleDateString("en-us", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  });
  const handleFrequencyChange = (value: number | null) => {
    if (value) setNewFrequency(value);
  };

  const handleFrequencySubmit = () => {
    intervalUpdator.mutate(newFrequency, {
      onSuccess() {
        notify({
          title: "Ship Frequency Updated",
          message: `The subscription will now ship every ${newFrequency} month(s)`,
        });
      },
      onError() {
        notify({
          title: "Error",
          message: `An error occurred. Please try again.`,
          severity: "error",
        });
      },
    });
  };

  return (
    <div className="w-full space-y-4">
      <IntervalSelector
        label="Change Frequency"
        value={newFrequency}
        onChange={handleFrequencyChange}
      />
      <p className="body text-sm">
        Based on the date and frequency selected above, the next three ship
        dates are scheduled for:
      </p>
      <ul className="ml-5 text-sm">
        {upcomingDateStrings.map((dateString) => {
          return (
            <li key={dateString} className="list-disc">
              {dateString}
            </li>
          );
        })}
      </ul>
      <Button
        className="w-full"
        disabled={frequency === newFrequency}
        onClick={handleFrequencySubmit}
      >
        Save Frequency
      </Button>
    </div>
  );
};

export const UpdateIntervalPage = ({ notify }: UpdateIntervalPageProps) => {
  const order = useSelectedItemContext().boxItem;
  const nextChargeDateUpdator = useChargeDate();

  const currFrequency = order.rechargeItem.cadence?.frequency;
  const currDate = order.rechargeItem.nextChargeDate;
  const [newDate, setNewDate] = useState(currDate);

  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value + "T00:00:00");
    if (!isNaN(+newDate)) {
      setNewDate(newDate);
    }
  };

  const handleDateSubmit = () => {
    nextChargeDateUpdator.mutate(new Date(newDate), {
      onSuccess: () => {
        notify({
          title: "Ship Date Updated",
          message: `Item scheduled for ${new Date(newDate).toLocaleDateString(
            "en-US"
          )}`,
        });
      },
      onError: () => {
        notify({
          title: "Error",
          message: `An error occurred. Please try again.`,
          severity: "error",
        });
      },
    });
  };

  return (
    <>
      <div className="w-full space-y-4">
        <DatePicker
          name="next-shipment-date"
          label="Change Ship Date"
          value={getIsoDateFormat(newDate)}
          onChange={handleDateChange}
          min={getIsoDateFormat(new Date())}
          className="appearance-none"
        />
        <Button
          className="w-full"
          disabled={currDate === newDate}
          onClick={handleDateSubmit}
        >
          Save Date
        </Button>
      </div>
      {currFrequency ? (
        <ShipmentFrequency notify={notify} frequency={currFrequency} />
      ) : null}
    </>
  );
};
